import { Button } from '@mui/material'
import { forwardRef } from 'react'
import { TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { handleError, useMessage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'
import { validateEmail } from '@/core/validators'

export type MailRegisterFormProps = {
  onSubmitMailRegister: (inputs: MailRegisterFormValues) => Promise<void>
}

export type MailRegisterFormValues = {
  email: string
}

const schema: UseFormProps['schema'] = o => ({
  email: validateEmail(o, 'mailRegister.email'),
})

export const MailRegisterForm: Organism<MailRegisterFormProps> = ({
  onSubmitMailRegister,
}) => {
  const { showError } = useMessage()
  const {
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<MailRegisterFormValues>({
    schema,
  })

  const handleOnSubmit = handleSubmit(values =>
    onSubmitMailRegister(values).catch(error =>
      handleError<MailRegisterFormValues>(error, setError, showError)
    )
  )

  return (
    <Form onSubmit={handleOnSubmit} sx={{ pt: 3 }}>
      <TextInputCell type="text" error={errors?.email} {...register('email')} />
      <ButtonCell loading={isSubmitting} />
    </Form>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('mailRegister')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

type ButtonCellProps = {
  loading: boolean
}
const ButtonCell: Cell<ButtonCellProps> = ({ loading }) => {
  const { t } = useTranslation('mailRegister')
  return (
    <CellFrame sx={{ textAlign: 'center' }}>
      <Button type="submit" size="large" sx={{ width: 244 }} loading={loading}>
        {t('submit')}
      </Button>
    </CellFrame>
  )
}
